import { AuthContext } from 'features/Auth/contexts/AuthContext';
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';

const AppSSEContext = createContext(null);

const RETRY_DELAYS = [1000, 2000, 5000, 15000, 30000];
const FAILURE_THRESHOLD = 5;

// Content dedupe. Measured on DEV (2026-09-22), the broker never carried one
// frame twice; this guard covers what the broker cannot see — a second
// EventSource, a proxy retry, a future backend double emission. Two
// byte-identical frames this close together are one event, not two.
const SSE_DEDUPE_WINDOW_MS = 3000;
const SSE_DEDUPE_MAX_ENTRIES = 500;
// Event names where an identical repeat inside the window is normal traffic:
// the same user typing in the same conversation sends the same frame again.
const SSE_DEDUPE_EXEMPT_EVENT_TYPES = new Set(['typing-started', 'typing-stopped']);

const SUPPRESSED_EVENT_TYPES = new Set([
  'keep-alive',
  'geolocation-trigger',
  'personnel-update',
  'messages-read',
]);

// `keep-alive` is the server heartbeat: every one of them is the same frame.
const isDedupeExempt = (eventType) =>
  eventType === 'keep-alive' || SSE_DEDUPE_EXEMPT_EVENT_TYPES.has(eventType);

// Keep event-stream diagnostics disabled by default: device simulators can emit
// enough updates to overwhelm the browser console. Uncomment while debugging SSE.
const logEventStream = (...args) => {
  // console.log('[AppSSE]', ...args);
};

/**
 * Single shared SSE connection for the entire app.
 * All features subscribe to this instead of opening their own EventSource.
 * Only connects when authenticated (pass enabled={isAuthenticated}).
 */
export function AppSSEProvider({ children, enabled = true }) {
  const [isConnected, setIsConnected] = useState(false);
  const esRef = useRef(null);
  const listenersRef = useRef(new Map());
  const closedRef = useRef(false);
  const attemptRef = useRef(0);
  const failureCountRef = useRef(0);
  const recoveryPausedRef = useRef(false);
  const reconnectTimerRef = useRef(null);
  // Bumped on every effect run. A reconnect timer or a recovery promise armed by
  // an earlier run carries the old number and stops before it opens a second
  // EventSource into the same listener map.
  const generationRef = useRef(0);
  // Raw frame -> arrival time, for the content dedupe below.
  const recentRef = useRef(new Map());
  const droppedRef = useRef(0);

  // Read the recovery callback from auth context. Stash in a ref so the
  // connection-management effect doesn't need it as a dependency (which
  // would tear down and rebuild the EventSource on every render).
  const { recoverSseStaleSession } = useContext(AuthContext) ?? {};
  const recoverSseStaleSessionRef = useRef(recoverSseStaleSession);
  useEffect(() => {
    recoverSseStaleSessionRef.current = recoverSseStaleSession;
  }, [recoverSseStaleSession]);

  useEffect(() => {
    if (!enabled) return;

    generationRef.current += 1;
    const generation = generationRef.current;
    const isCurrentGeneration = () => generation === generationRef.current;

    closedRef.current = false;
    failureCountRef.current = 0;
    recoveryPausedRef.current = false;

    const buildUrl = () => `${process.env.PUBLIC_API_URL}/event-stream`;
    const eventStreamUrl = buildUrl();
    logEventStream('provider enabled', { url: eventStreamUrl });

    const cleanup = () => {
      logEventStream('cleanup connection');
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      if (esRef.current) {
        esRef.current.close();
        esRef.current = null;
      }
      setIsConnected(false);
    };

    const scheduleReconnect = () => {
      if (closedRef.current || !isCurrentGeneration()) return;
      cleanup();
      failureCountRef.current += 1;

      if (recoveryPausedRef.current) {
        logEventStream('reconnect paused — session recovery in progress');
        return;
      }

      if (
        failureCountRef.current >= FAILURE_THRESHOLD &&
        typeof recoverSseStaleSessionRef.current === 'function'
      ) {
        recoveryPausedRef.current = true;
        logEventStream('triggering session recovery', {
          failures: failureCountRef.current,
        });

        Promise.resolve(recoverSseStaleSessionRef.current())
          .then((result) => {
            // A recovery started before the last `enabled` flip settles into a
            // connection this run already made. Let it resolve and do nothing.
            if (!isCurrentGeneration()) return;
            // 'recovered'      refresh worked — start a fresh connect attempt
            // 'network-error'  not an auth issue; resume retries silently
            // 'auth-failed'    stay paused; toast/grace-timer drives outcome
            // 'in-flight'      another caller is handling it; stay paused
            if (result === 'recovered' || result === 'network-error') {
              failureCountRef.current = 0;
              attemptRef.current = 0;
              recoveryPausedRef.current = false;
              if (!closedRef.current) connect();
            }
          })
          .catch((err) => {
            if (!isCurrentGeneration()) return;
            logEventStream('recovery threw', err);
            recoveryPausedRef.current = false;
          });
        return;
      }

      const delay = RETRY_DELAYS[Math.min(attemptRef.current, RETRY_DELAYS.length - 1)] ?? 1000;
      attemptRef.current += 1;
      logEventStream('schedule reconnect', {
        delay,
        attempt: attemptRef.current,
      });
      reconnectTimerRef.current = setTimeout(connect, delay);
    };

    // Drops entries that have aged out of the window, then trims the map to its
    // cap. Insertion order is arrival order (a re-seen key is deleted before it
    // is re-added), so the age walk can stop at the first live entry and the
    // trim always removes the oldest.
    const pruneRecent = (now) => {
      const recent = recentRef.current;
      for (const [key, seenAt] of recent) {
        if (now - seenAt < SSE_DEDUPE_WINDOW_MS) break;
        recent.delete(key);
      }
      while (recent.size > SSE_DEDUPE_MAX_ENTRIES) {
        recent.delete(recent.keys().next().value);
      }
    };

    // The raw frame is the identity: `evt.data` is the exact string the server
    // wrote. Re-serialising the parsed object would make two frames that differ
    // only in key order compare equal.
    const isDuplicateFrame = (eventType, evt) => {
      if (isDedupeExempt(eventType)) return false;

      const key = eventType + '\n' + evt?.data;
      const now = Date.now();
      const seenAt = recentRef.current.get(key);

      if (seenAt !== undefined && now - seenAt < SSE_DEDUPE_WINDOW_MS) {
        droppedRef.current += 1;
        logEventStream('duplicate dropped', {
          type: eventType,
          keyLength: key.length,
          dropped: droppedRef.current,
        });
        return true;
      }

      recentRef.current.delete(key);
      recentRef.current.set(key, now);
      pruneRecent(now);
      return false;
    };

    const dispatch = (eventType, evt) => {
      if (isDuplicateFrame(eventType, evt)) return;

      try {
        const data = JSON.parse(evt.data);
        if (!SUPPRESSED_EVENT_TYPES.has(eventType)) {
          const subscribers = listenersRef.current.get(eventType) || [];
          // `subscribers: 0` while data arrives means the event IS reaching this
          // client but nothing is listening (subscription timing/scope) — vs. the
          // line never printing at all, which means it isn't arriving here.
          logEventStream('event', {
            type: eventType,
            // Envelope convention is { name, data, info } — surface `name` so an
            // event delivered under the default `message` channel is still visible.
            name: data?.name,
            data,
            raw: evt?.data,
            subscribers: subscribers.length,
          });
        }
        (listenersRef.current.get(eventType) || []).forEach((cb) => cb(data));
      } catch (err) {
        logEventStream('event parse error', {
          type: eventType,
          rawData: evt?.data,
          error: err,
        });
        console.error(`[AppSSE] parse error on ${eventType}:`, err);
      }
    };

    const connect = () => {
      if (closedRef.current || !isCurrentGeneration()) return;
      try {
        // Two open sources is never valid: both would feed the same listener
        // map, so every event would arrive twice.
        if (esRef.current) {
          esRef.current.close();
          esRef.current = null;
        }
        logEventStream('connect start', { url: eventStreamUrl });
        const es = new EventSource(eventStreamUrl, { withCredentials: true });
        esRef.current = es;

        es.addEventListener('open', () => {
          attemptRef.current = 0;
          failureCountRef.current = 0;
          recoveryPausedRef.current = false;
          setIsConnected(true);
          logEventStream('connection open');
        });

        es.addEventListener('error', () => {
          logEventStream('connection error');
          scheduleReconnect();
        });

        // Dispatch all named events to subscribers
        [
          'keep-alive',
          // Alarms
          'alarm-add',
          'alarm-update',
          // Alerts
          'alert-add',
          'alert-update',
          'alert-remove',
          // FEMA IPAWS (FE-03): a message's status changed — workplace-scoped on the backend
          'ipaws-message-update',
          // Devices / integrations
          'device-update',
          'device-update-user',
          'device-add',
          'device-remove',
          'group-update',
          'group-add',
          'group-remove',
          'system-update',
          'system-add',
          'system-remove',
          // Users / roles
          'user-roles-updated',
          'role-perms-updated',
          // Custom Weather parser completion
          'cw-current-conditions-finished',
          'cw-buoy-report-finished',
          'cw-hourly-forecast-48hr-finished',
          'cw-hourly-forecast-finished',
          'cw-detailed-fire-danger-finished',
          'cw-extended-fire-danger-finished',
          'cw-detailed-forecast-5day-finished',
          'cw-airport-delay-finished',
          'cw-15day-expanded-forecast-finished',
          'cw-detailed-marine-7day-finished',
          'cw-astronomy-forecast-ext-finished',
          'cw-tide-forecast-finished',
          'cw-one-extended-marine-finished',
          'cw-air-quality-obs-finished',
          'cw-air-quality-for-finished',
          // Personnel / Responder
          'personnel-update',
          'geolocation-trigger',
          // Activities / Schedules
          'activity-update',
          'activities-update',
          'activity-created',
          'activity-deleted',
          'assignment-update',
          'assignment-updated',
          'assignments-update',
          'assignment-created',
          'assignment-deleted',
          // Tours
          'tour-update',
          // VoIP / Voice calls
          'call-started',
          'call-ended',
          'call-missed',
          'voice-call',
          'call-participant-joined',
          'call-participant-left',
          // Messaging
          'conversation-created',
          'participant-joined',
          'participant-left',
          'message-sent',
          'message-edited',
          'message-deleted',
          'messages-read',
          'typing-started',
          'typing-stopped',
          'reaction-added',
          'reaction-removed',
          'key-reencryption-request',
          'key-available',
          // Legacy unnamed message event. This entry is the ONE path for a frame
          // with no `event:` line — an `es.onmessage` handler as well would
          // deliver every unnamed event to every subscriber twice.
          'message',
        ].forEach((type) => {
          es.addEventListener(type, (evt) => dispatch(type, evt));
        });
      } catch (err) {
        logEventStream('connect exception', err);
        scheduleReconnect();
      }
    };

    connect();

    return () => {
      closedRef.current = true;
      logEventStream('provider disabled');
      cleanup();
    };
  }, [enabled]);

  const subscribe = useCallback((eventType, callback) => {
    if (!listenersRef.current.has(eventType)) {
      listenersRef.current.set(eventType, []);
    }
    listenersRef.current.get(eventType).push(callback);

    return () => {
      const list = listenersRef.current.get(eventType) || [];
      const idx = list.indexOf(callback);
      if (idx >= 0) list.splice(idx, 1);
    };
  }, []);

  // Dev-only: fire a synthetic event through the same listener pipeline as real
  // SSE. It calls the subscribers directly, so it bypasses the content dedupe in
  // `dispatch` — publishing the same payload twice on purpose has to deliver it
  // twice.
  const publish = useCallback((eventType, data) => {
    if (process.env.NODE_ENV !== 'development') return;
    (listenersRef.current.get(eventType) || []).forEach((cb) => cb(data));
  }, []);

  return (
    <AppSSEContext.Provider value={{ isConnected, subscribe, publish }}>
      {children}
    </AppSSEContext.Provider>
  );
}

export function useAppSSE() {
  const ctx = useContext(AppSSEContext);
  if (!ctx) throw new Error('useAppSSE must be used within AppSSEProvider');
  return ctx;
}

// Non-throwing variant for providers that may render without an AppSSEProvider
// above them (e.g. PermsProvider in isolated tests). Returns null when there is
// no SSE connection so the caller can no-op instead of crashing.
export function useAppSSEOptional() {
  return useContext(AppSSEContext);
}
